import { useCallback, useEffect, useState } from 'react';
import { supportsViewTransitions, withViewTransition } from '../utils/viewTransitions';

/**
 * Custom hook for managing view transitions
 * @param {Object} options - Configuration options
 * @returns {Object} - Transition utilities and state
 */
export const useViewTransition = (options = {}) => {
  const {
    onTransitionStart,
    onTransitionEnd,
    fallbackAnimation = true
  } = options;

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [supportsAPI, setSupportsAPI] = useState(false);

  useEffect(() => {
    setSupportsAPI(supportsViewTransitions());
  }, []);

  /**
   * Execute a function with view transition
   */
  const executeWithTransition = useCallback(async (updateFn, transitionOptions = {}) => {
    setIsTransitioning(true);
    onTransitionStart?.();

    try {
      await withViewTransition(updateFn, {
        ...transitionOptions,
        skipTransition: !supportsAPI && !fallbackAnimation
      });
    } finally {
      setIsTransitioning(false);
      onTransitionEnd?.();
    }
  }, [supportsAPI, fallbackAnimation, onTransitionStart, onTransitionEnd]);

  return {
    executeWithTransition,
    isTransitioning,
    supportsViewTransitions: supportsAPI,
    // Utility to wrap any state setter
    createTransitionSetter: useCallback((setter) => {
      return async (value) => {
        await executeWithTransition(() => {
          setter(value);
        });
      };
    }, [executeWithTransition])
  };
};

/**
 * Hook specifically for theme transitions
 */
export const useThemeTransition = () => {
  const { executeWithTransition, isTransitioning, supportsViewTransitions: supportsAPI } = useViewTransition();

  const transitionTheme = useCallback(async (toggleFn) => {
    if (!supportsAPI) {
      toggleFn();
      return;
    }

    document.documentElement.classList.add('theme-transitioning');

    await executeWithTransition(toggleFn);

    document.documentElement.classList.remove('theme-transitioning');
  }, [executeWithTransition, supportsAPI]);

  return {
    transitionTheme,
    isTransitioning
  };
};

/**
 * Hook for navigation transitions
 */
export const useNavigationTransition = () => {
  const { executeWithTransition, isTransitioning } = useViewTransition();

  const transitionTo = useCallback(async (navigationFn, direction = 'forward') => {
    document.documentElement.setAttribute('data-navigation-direction', direction);

    await executeWithTransition(navigationFn);

    document.documentElement.removeAttribute('data-navigation-direction');
  }, [executeWithTransition]);

  return {
    transitionTo,
    isNavigating: isTransitioning
  };
};

/**
 * Hook for cart transitions
 */
export const useCartTransition = () => {
  const { executeWithTransition, isTransitioning } = useViewTransition();

  const transitionCartAction = useCallback(async (actionFn, action = 'add') => {
    document.documentElement.setAttribute('data-cart-action', action);

    try {
      await executeWithTransition(actionFn);
    } finally {
      document.documentElement.removeAttribute('data-cart-action');
    }
  }, [executeWithTransition]);

  return {
    transitionCartAction,
    isUpdating: isTransitioning
  };
};
